import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import ReactPlayer from 'react-player';
import { connect } from 'react-redux';

import API from 'api';
import { mapDispatchProps, mapStateToProps } from 'store/store';
import { useLanguage } from 'assets/languages';

import Button from 'components/button';
import Checkbox, { ICON_TYPES } from 'components/checkbox';
import TextArea from 'components/textArea';
import GUIDELINE_RULES from 'constants/guidelineRules';
import useIsMobile from 'hooks/useIsMobile/useIsMobile';
import GuidelineRulesModal from 'pages/campaigns/components/guidelineRulesModal/guidelineRulesModal';
import FeedbackRemainTimer from './components/feedbackRemainTimer';
import { ROUTES } from 'routes';

const PostingFeedback: React.FC = (props: any) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const language = useLanguage();
  const languageTexts = {
    ...language.common,
    ...language.pages.inspection_feedback,
  };

  const MODAL_TYPE = {
    PREVIEW: 'PREVIEW',
  };

  const [posting, setPosting] = useState<any>(null);

  const [capturedImages, setCapturedImages] = useState<{ url: string; timestamp: string; msg: string }[]>([]);

  const [inspectionFeedbackGuidelineMessage, setInspectionFeedbackGuidelineMessage] = useState<any>(null);

  const [subtitleFontMessage, setSubtitleFontMessage] = useState<any>(null);
  const [subtitleSizeMessage, setSubtitleSizeMessage] = useState<any>(null);

  const [backgroundMusicCheckbox, setBackgroundMusicCheckbox] = useState<any>(null);
  const [backgroundMusicMessage, setBackgroundMusicMessage] = useState<any>(null);
  const [narrationAddCheckbox, setNarrationAddCheckbox] = useState<any>(null);
  const [narrationAddMessage, setNarrationAddMessage] = useState<any>(null);
  const [narrationEditCheckbox, setNarrationEditCheckbox] = useState<any>(null);
  const [narrationEditMessage, setNarrationEditMessage] = useState<any>(null);

  const [previewImage, setPreviewImage] = useState<any>(null);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);

  const params = useParams();
  const playerRef = useRef<any>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [videoReady, setVideoReady] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);

  const [dataUpdatedAt, setDataUpdatedAt] = useState(null); // force rendering no states(ref) variables

  const getIsInspectionExpired = (posting: any) => {
    return dayjs().isBefore(dayjs(posting?.rental?.inspectionFeedbackDueDt).endOf('day')) == false;
  };

  const [isInspectionFeedbackDueDateExpired, setIsInspectionFeedbackDueDateExpired] = useState(undefined);
  const postingRef = useRef(posting);
  const campaign = useRef<any>(null);
  const guidelines = useRef<any>(null);
  const product = useRef<any>(null);

  const isDisabled = postingRef.current?.isInspectionProcessCompleted || isInspectionFeedbackDueDateExpired;

  const hasFeedback = (
    (inspectionFeedbackGuidelineMessage != null && inspectionFeedbackGuidelineMessage.trim().length > 0) ||
    (subtitleFontMessage != null && subtitleFontMessage.trim().length > 0) ||
    (subtitleSizeMessage != null && subtitleSizeMessage.trim().length > 0) ||
    (backgroundMusicMessage != null && backgroundMusicMessage.trim().length > 0) ||
    (narrationAddMessage != null && narrationAddMessage.trim().length > 0) ||
    (narrationEditMessage != null && narrationEditMessage.trim().length > 0) ||
    capturedImages.length > 0
  );

  useEffect(() => {
    fetchPosting();
  }, [params.id, props.auth.token]);

  useEffect(() => {
    postingRef.current = posting;
    if (posting != null) {
      fetchData();
    }
  }, [posting]);

  const fetchData = async () => {
    await fetchCampaign();
    await fetchProduct();
    await fetchCampaignGuidelineRules();
    setDataUpdatedAt(dayjs().format('YYYY-MM-DD HH:mm:ss'));
  };

  const isRequiredPartnershipCode = useMemo(() => {
    if (!guidelines.current) {
      return false;
    }
    if (
      guidelines.current.find((guideline: any) => {
        return (
          guideline.groupName === GUIDELINE_RULES.GROUP_NAMES.REQUIRE_PARTNERSHIP_CODE &&
          guideline.value === GUIDELINE_RULES.OPTIONS_REQUIRE_PARTNERSHIP_CODE.REQUIRED
        );
      })
    ) {
      return true;
    }

    return false;
  }, [guidelines.current]);

  const fetchCampaign = async () => {
    return await API.Campaign.show({
      campaignId: posting?.campaignId,
      brandToken: props.auth.token,
      appends: ['seeding_pass_payment'],
    })
      .then((response) => {
        campaign.current = response.data.campaign;
      })
      .catch((error) => {
        console.error('fetchCampaign error', error);
      });
  };

  const fetchCampaignGuidelineRules = async () => {
    return await API.GuidelineRules.index({
      campaignId: posting?.campaignId,
    })
      .then((response) => {
        guidelines.current = response.data.rules;
      })
      .catch((error) => {
        console.error('fetchCampaignGuidelineRules error', error);
      });
  };

  const fetchProduct = async () => {
    return await API.Product.show({
      id: posting?.productId,
      brandToken: props.auth.token,
      with: ['product_master'],
    })
      .then((response) => {
        product.current = response.data.product;
      })
      .catch((error) => {
        props.showModal({ text: error.message });
        history.back();
      });
  };

  const handlePreviewModal = (element, eventType, img = null) => {
    switch (element) {
      case MODAL_TYPE.PREVIEW:
        if (eventType === 'start') {
          if (isPreviewVisible) return;
          setPreviewImage(img);
          setIsPreviewVisible(true);
        } else {
          if (!isPreviewVisible) return;
          setIsPreviewVisible(false);
          setPreviewImage(null);
        }
        break;
      default:
        break;
    }
  };

  const fetchPosting = async () => {
    const id = params.id;
    const response = await API.Post.show({
      brandToken: props.auth.token,
      postingId: id,
    });

    const posting = response.data.posting;

    setPosting(posting);

    setVideoReady(false);
    setIsInspectionFeedbackDueDateExpired(getIsInspectionExpired(posting));

    setInspectionFeedbackGuidelineMessage(posting?.inspectionFeedbacksGuideline?.from);

    setSubtitleFontMessage(posting?.inspectionFeedbacksSubtitleFont?.from);
    setSubtitleSizeMessage(posting?.inspectionFeedbacksSubtitleSize?.from);

    setBackgroundMusicMessage(posting?.inspectionFeedbacksBackgroundMusicEdit?.from);

    setNarrationAddCheckbox(posting?.inspectionFeedbacksNarrationAdd?.from);
    setNarrationAddMessage(posting?.inspectionFeedbacksNarrationAdd?.from);


    setNarrationEditCheckbox(posting?.inspectionFeedbacksNarrationEdit?.from);
    setNarrationEditMessage(posting?.inspectionFeedbacksNarrationEdit?.from);

    setCapturedImages(
      posting?.inspectionFeedbacksSequence
        ? posting?.inspectionFeedbacksSequence?.map((item: any) => {
            const decodedData = JSON.parse(item.guidelineRule.value);

            return {
              url: decodedData.url,
              timestamp: decodedData.timestamp,
              msg: decodedData.msg,
            };
          })
        : [],
    );
  };

  // Helper to convert dataURL to File
  function dataURLtoFile(dataurl: string, filename: string): File {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)![1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  }

  const handleCaptureFrame = async () => {
    if (!playerRef.current) return;
    if (!videoReady) return;
    if (isDisabled) return;

    const video: HTMLVideoElement | null = playerRef.current.getInternalPlayer();
    if (video && video instanceof HTMLVideoElement) {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const targetWidth = 280;
      const targetHeight = 500;
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0, video.videoWidth, video.videoHeight, 0, 0, targetWidth, targetHeight);
        const dataURL = canvas.toDataURL('image/jpeg', 0.7);
        const file = dataURLtoFile(dataURL, `capture_${Date.now()}.jpg`);
        const formData = new FormData();
        formData.append('brand_token', props.auth.token);
        formData.append('image', file);
        try {
          const res: any = await API.InspectionFeedback.uploadImage(formData);

          const host = 'https://nmodelin20.s3.ap-northeast-2.amazonaws.com/inspection-feedback/';
          const url = `${host}${res.data.url}`;

          setCapturedImages((prev) => [
            ...prev,
            {
              url: url,
              basename: res.data.url,
              timestamp: getCurrentVideoTimestamp(),
              msg: '',
            },
          ]);
        } catch (e) {
          console.error('Image upload failed', e);
        }
      }
    }
  };

  const getCurrentVideoTimestamp = () => {
    const t =
      playerRef.current && typeof playerRef.current.getCurrentTime === 'function'
        ? playerRef.current.getCurrentTime()
        : currentTime;
    return `${Math.floor(t / 60)
      .toString()
      .padStart(2, '0')}:${Math.floor(t % 60)
      .toString()
      .padStart(2, '0')}`;
  };

  const showSaveModal = () => {
    props.showModal({
      hideCloseButton: true,
      title: languageTexts.inspection_save_modal_title,
      text: languageTexts.inspection_save_modal_text,
      cancel: () => {},
      confirm: async () => {
        await saveFeedbacks();
      },
    });
  };

  const showStoreModal = () => {
    const message = {
      title: '',
      text: '',
    };
    if (hasFeedback) {
      message.title = languageTexts.inspection_store_modal_decline_feedback_title;
      message.text = languageTexts.inspection_store_modal_decline_feedback_text;
    } else {
      message.title = languageTexts.inspection_store_modal_approve_feedback_title;
      message.text = languageTexts.inspection_store_modal_approve_feedback_text;
    }
    
    props.showModal({
      hideCloseButton: true,
      title: message.title,
      text: message.text,
      cancel: () => {},
      confirm: async () => {
        const res = await submitFeedbacksAndRefetch();
        if (res?.message) {
          props.showModal({
            text: res?.message,
          });
        }
      },
    });
  };

  const saveFeedbacks = async () => {
    try {
      let params: any = {
        brandToken: props.auth.token,
        hasFeedback: hasFeedback,
        postingId: posting.id,
        capturedImages: capturedImages,
        inspectionFeedbackGuidelineMessage: inspectionFeedbackGuidelineMessage,
        subtitleFontMessage: subtitleFontMessage,
        subtitleSizeMessage: subtitleSizeMessage,
        backgroundMusicMessage: backgroundMusicMessage,
        narrationAddMessage: narrationAddMessage,
        narrationEditMessage: narrationEditMessage,
      };
      const response = await API.InspectionFeedback.store(params);

      props.showModal({
        hideCloseButton: true,
        text: response.message,
        confirm: () => {
          props.closeModal();
          navigate({
            pathname: ROUTES.CAMPAIGN_PRODUCT_SHOW.SHOW(posting.campaignId, posting.productId),
            search: `?tab=postings`,
          });
        },
      });
    } catch (e) {
      console.error(e);
      props.showModal({
        hideCloseButton: true,
        text: e.message,
      });
    }
  };

  const submitFeedbacksAndRefetch = async () => {
    try {
      let params: any = {
        brandToken: props.auth.token,
        hasFeedback: hasFeedback,
        postingId: posting.id,
        capturedImages: capturedImages,
        inspectionFeedbackGuidelineMessage: inspectionFeedbackGuidelineMessage,
        subtitleFontMessage: subtitleFontMessage,
        subtitleSizeMessage: subtitleSizeMessage,
        backgroundMusicMessage: backgroundMusicMessage,
        narrationAddMessage: narrationAddMessage,
        narrationEditMessage: narrationEditMessage,
      };

      await API.InspectionFeedback.store(params);
      const response = await API.InspectionFeedback.submit(params);
      await fetchPosting();
      await fetchData();

      return response;
    } catch (e) {
      console.error(e);
      return e;
    }
  };

  const handleFeedbackDueDateExpired = async () => {
    if (isInspectionFeedbackDueDateExpired) {
      return;
    }
    setIsInspectionFeedbackDueDateExpired(true);
    
    props.showModal({
      title: languageTexts.inspection_overdue_time,
      text: <>{languageTexts.inspection_overdue_text}</>,
      confirm: () => {},
    });
  }; 

  const headerHeight = props.preferences.headerHeight;

  const renderPageTitle = (params: {
    isInspectionProcessCompleted: boolean;
    inspectionFeedbackDueDt: string;
  }) => {
    const isRequirementsFetched = (
      params.isInspectionProcessCompleted != null &&
      params.inspectionFeedbackDueDt != null
    );
    if (!isRequirementsFetched) {
      return null; // Posting 패치 미완료 판정
    }

    // 검수 종료 타이틀
    if (params.isInspectionProcessCompleted) {
      return (
        <div className="text-[24px] font-bold">
          {languageTexts.inspection_title_completed}
        </div>
      );
    }

    // 검수 남은 시간 타이틀
    return (
      <FeedbackRemainTimer
        render={(remainTimeStr) => (
          <div className="text-[24px] font-bold">
            {languageTexts.inspection_remain_time(remainTimeStr)}
          </div>
        )}
        dueDate={params.inspectionFeedbackDueDt}
        onExpired={() => {
          handleFeedbackDueDateExpired();
        }}
      />
    );
  };

  const renderShowGuidelineButton = (params: {
    productId: number | null | undefined;
    isMobile: boolean;
    showOverlays: any;
  }) => {
    if (params.productId == null) {
      return null;
    }
    return (
      <div className="flex flex-row items-end">
        {(params.productId != null) && (
          <div
            className="font-bold underline cursor-pointer text-[15px]"
            onClick={() => {
              let containerStyle =
                'flex flex-col w-full px-5 py-5 bg-white overflow-scroll max-w-[480px]' +
                (params.isMobile ? ' h-full' : ' rounded-2xl');

              params.showOverlays({
                className: containerStyle,
                content: () => (
                  <GuidelineRulesModal productId={params.productId} />
                ),
              });
            }}
          >
            {languageTexts.show_guidelines}
          </div>
        )}
      </div>
    )
  }

  if (posting == null || dataUpdatedAt == null) {
    return null;
  }

  return (
    <div
      className={`flex flex-col px-5 lg:px-8 xl:px-0 mx-auto md:w-[800px] xl:w-[1024px] mt-[100px] gap-2`}
      style={{ top: `${headerHeight}px` }}
    >
      <div className="flex flex-row gap-[50px] min-w-[1024px] h-full mb-[250px]">
        <div className="flex flex-col overflow-y-auto max-h-full w-full">
          <div className="flex flex-row justify-between">
            {renderPageTitle({
              inspectionFeedbackDueDt: posting?.rental?.inspectionFeedbackDueDt,
              isInspectionProcessCompleted: posting?.isInspectionProcessCompleted,
            })}

            {renderShowGuidelineButton({
              productId: posting?.productId,
              isMobile: props.preferences.isMobile,
              showOverlays: props.showOverlays,
            })}
          </div>

          <div className="mt-[50px] text-[15px] whitespace-pre-wrap">
            {languageTexts.feedback_introduction}
          </div>

          <div className="mt-[50px] font-bold text-[17px]">
            {languageTexts.inspection_capture_image_title}
          </div>

          <canvas ref={canvasRef} style={{ display: 'none' }} />

          <div className="mt-[15px] flex flex-row border border-[#E8E8E8]">
            <div className="p-[20px] border-r border-[#E8E8E8]">
              <ReactPlayer
                ref={playerRef}
                key={posting?.id}
                url={posting?.images?.[0]?.thumbSmUrl}
                controls
                width={280}
                height={500}
                style={{
                  minWidth: 280,
                  minHeight: 500,
                }}
                config={{
                  file: {
                    attributes: {
                      crossOrigin: 'anonymous',
                    },
                  },
                }}
                progressInterval={1000}
                onProgress={(state: any) => {
                  const seconds = Math.floor((state?.playedSeconds ?? 0));
                  setCurrentTime((prev) => (prev !== seconds ? seconds : prev));
                }}
                onReady={() => setVideoReady(true)}
              />

              {isDisabled ? null : (
                <div className="mt-0.5">
                  <Button
                    onClick={handleCaptureFrame}
                    className="py-3 mx-auto flex justify-center items-center rounded-0 select-none"
                    buttonColor={'black'}
                    textColor={'white'}
                    textClassName={'text-[15px] font-bold'}
                    text={languageTexts.inspection_capture_current_sequence(getCurrentVideoTimestamp())}
                  />
                </div>
              )}
            </div>
            <div className="p-[20px] w-full">
              {(capturedImages?.length > 0) ? (
                <div className="mt-4 flex flex-col flex-wrap gap-2">
                  {capturedImages.map((img, idx) => (
                    <div className="flex flex-row" key={img.url + '-' + idx}>
                      <div className="flex flex-col items-center relative">
                        <img
                          key={idx}
                          src={img.url}
                          alt={`Captured frame ${idx + 1}`}
                          style={{ maxWidth: 84.35, height: 150, border: '1px solid #ccc', cursor: 'pointer' }}
                          onClick={() => {
                            if (!isPreviewVisible || !previewImage || previewImage.url !== img.url) {
                              handlePreviewModal(MODAL_TYPE.PREVIEW, 'start', img);
                            }
                          }}
                          onMouseOver={() => {
                            if ((!isPreviewVisible || !previewImage || previewImage.url !== img.url) && !isMobile) {
                              handlePreviewModal(MODAL_TYPE.PREVIEW, 'start', img);
                            }
                          }}
                        />
                        {isDisabled ? null : (
                          <div
                            className="absolute border-grey-400 cursor-pointer"
                            style={{ right: 0, top: 0 }}
                            onClick={() => {
                              if (isDisabled) return;
                              setCapturedImages((prev) => prev.filter((_, i) => i !== idx));
                            }}
                          >
                            <img className="w-[22px] h-[22px]" src="/assets/icons/ic-close-black-squared.svg" />
                          </div>
                        )}

                        <div className="flex flex-row items-center pt-1.5 pb-2.5">
                          <span className="text-[15px] font-bold">{img.timestamp}</span>
                        </div>
                      </div>

                      <div className="flex flex-col w-full justify-around">
                        <div className="flex flex-row w-full h-full">
                          <TextArea
                            height={150}
                            disabled={isDisabled}
                            className="w-full h-full"
                            bordered
                            style={{ height: '100%' }}
                            inputStyle={{ height: '100%' }}
                            // height={'100%'}
                            placeholder={languageTexts.input}
                            value={img.msg ?? ''}
                            onChangeText={(text) => {
                              if (isDisabled) return;
                              setCapturedImages((prev) => {
                                const newImages = [...prev];
                                newImages[idx].msg = text;
                                return newImages;
                              });
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="w-full h-full flex flex-col justify-center items-center">
                  <span className="text-[20px] text-[#999999]">
                    {languageTexts.inspection_capture_no_image}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* 가이드라인 위반 내역 */}
          {isDisabled && !inspectionFeedbackGuidelineMessage ? null : (
            <div className="flex flex-col mt-[50px]">
              <div className="flex flex-row justify-between">
                <span className="font-bold text-[17px]">{languageTexts.inspection_feedback_guideline_title}</span>
                {renderShowGuidelineButton({
                  productId: posting?.productId,
                  isMobile: props.preferences.isMobile,
                  showOverlays: props.showOverlays,
                })}
              </div>

              <div className="mt-[15px]">
                <TextArea
                  bordered
                  disabled={isDisabled}
                  inputStyle={{
                    minHeight: '150px',
                  }}
                  placeholder={languageTexts.inspection_feedback_guideline_placeholder}
                  onChangeText={(text) => setInspectionFeedbackGuidelineMessage(text)}
                  value={inspectionFeedbackGuidelineMessage ?? ''}
                />
              </div>
            </div>
          )}

          {/* 자막 폰트 변경 */}
          {isDisabled && !subtitleFontMessage ? null : (
            <div className="flex flex-col mt-[50px]">
              <div className="flex flex-row">
                <span className="font-bold text-[17px]">{languageTexts.inspection_feedback_subtitle_font_title}</span>
              </div>

              <div className="mt-[15px]">
                <TextArea
                  bordered
                  disabled={isDisabled}
                  placeholder={languageTexts.inspection_feedback_subtitle_font_placeholder}
                  onChangeText={(text) => setSubtitleFontMessage(text)}
                  value={subtitleFontMessage ?? ''}
                />
              </div>
            </div>
          )}

          {/* 자막 크기 변경 */}
          {isDisabled && !subtitleSizeMessage ? null : (
            <div className="flex flex-col mt-[50px]">
              <div className="flex flex-row">
                <span className="font-bold text-[17px]">{languageTexts.inspection_feedback_subtitle_size_title}</span>
              </div>

              <div className="mt-[15px]">
                <TextArea
                  bordered
                  disabled={isDisabled}
                  placeholder={languageTexts.inspection_feedback_subtitle_size_placeholder}
                  onChangeText={(text) => setSubtitleSizeMessage(text)}
                  value={subtitleSizeMessage ?? ''}
                />
              </div>
            </div>
          )}

          {/* 배경음악 교체 */}
          {isDisabled && !backgroundMusicMessage ? null : (
            <div className="flex flex-col mt-[50px]">
              <div className="flex flex-row">
                <span className="font-bold text-[17px]">{languageTexts.inspection_feedback_background_music_title}</span>
              </div>
              <div className="flex flex-row">
                {isRequiredPartnershipCode ? (
                  <span className="mt-[10px] text-[15px] text-[#FA0000]">
                    {languageTexts.inspection_feedback_background_music_desc_with_partnership}
                  </span>
                ) : (
                  <span className="mt-[10px] text-[15px] text-[#999999]">
                    {languageTexts.inspection_feedback_background_music_desc_without_partnership}
                  </span>
                )}
              </div>

              {(isDisabled && !backgroundMusicMessage) ? null : (
                <div className="mt-[15px]">
                  <TextArea
                    bordered
                    disabled={isDisabled}
                    placeholder={languageTexts.inspection_feedback_background_music_placeholder}
                    onChangeText={(text) => setBackgroundMusicMessage(text)}
                    value={backgroundMusicMessage ?? ''}
                  />
                </div>
              )}
            </div>
          )}

          {/* 나레이션 추가 */}
          {isDisabled && !narrationAddMessage ? null : (
            <div className="flex flex-col mt-[50px]">
              <div className="flex flex-row">
                <span className="font-bold text-[17px]">{languageTexts.inspection_feedback_narration_add_title}</span>
              </div>
              <div className="flex flex-row">
                <span className="mt-[10px] text-[15px] text-[#999999]">
                  {languageTexts.inspection_feedback_narration_add_desc}
                </span>
              </div>

              {isDisabled ? null : (
                <div className="mt-[15px]">
                  <Checkbox
                    type={ICON_TYPES.DEFAULT}
                    checked={narrationAddCheckbox}
                    text={languageTexts.inspection_feedback_guideline_violate}
                    onClick={() => {
                      if (isDisabled) {
                        return;
                      }
                      const nextState = !narrationAddCheckbox;
                      setNarrationAddCheckbox(nextState);
                      if (!nextState) {
                        setNarrationAddMessage('');
                      }
                    }}
                  />
                </div>
              )}

              {narrationAddCheckbox ? (
                <div className="mt-[20px]">
                  <TextArea
                    bordered
                    disabled={isDisabled}
                    placeholder={languageTexts.inspection_feedback_narration_add_placeholder}
                    onChangeText={(text) => {
                      if (isDisabled) return;
                      setNarrationAddMessage(text);
                    }}
                    value={narrationAddMessage ?? ''}
                  />
                </div>
              ) : null}
            </div>
          )}

          {/* 나레이션 수정 */}
          {isDisabled && !narrationEditMessage ? null : (
            <div className="flex flex-col mt-[50px]">
              <div className="flex flex-row">
                <span className="font-bold text-[17px]">{languageTexts.inspection_feedback_narration_edit_title}</span>
              </div>
              <div className="flex flex-row">
                <span className="mt-[10px] text-[15px] text-[#999999]">
                  {languageTexts.inspection_feedback_narration_edit_desc}
                </span>
              </div>

              {isDisabled ? null : (
                <div className="mt-[15px]">
                  <Checkbox
                    type={ICON_TYPES.DEFAULT}
                    checked={narrationEditCheckbox}
                    text={languageTexts.inspection_feedback_guideline_violate}
                    onClick={() => {
                      if (isDisabled) return;
                      const nextState = !narrationEditCheckbox;
                      setNarrationEditCheckbox(nextState);
                      if (!nextState) {
                        setNarrationEditMessage('');
                      }
                    }}
                  />
                </div>
              )}

              {narrationEditCheckbox ? (
                <div className="mt-[20px]">
                  <TextArea
                    bordered
                    disabled={isDisabled}
                    placeholder={languageTexts.inspection_feedback_narration_edit_placeholder}
                    onChangeText={(text) => {
                      if (isDisabled) return;
                      setNarrationEditMessage(text);
                    }}
                    value={narrationEditMessage ?? ''}
                  />
                </div>
              ) : null}
            </div>
          )}

          {/* 나레이션 삭제 */}
          {isDisabled ? null : (
            <div className="flex flex-col mt-[50px]">
              <div className="flex flex-row">
                <span className="font-bold text-[17px]">{languageTexts.inspection_feedback_narration_delete_title}</span>
              </div>
              <div className="flex flex-row">
                <span className="mt-[10px] text-[15px] text-[#999999]">
                  {languageTexts.inspection_feedback_narration_delete_desc}
                </span>
              </div>
            </div>
          )}

          {isDisabled ? null : (
            <div className="">
              <div className="mt-[50px] flex flex-row justify-center gap-2.5">
                <Button
                  className="py-3 max-w-[280px] flex flex-row justify-center items-center" 
                  buttonColor={'white'}
                  textColor={'black'}
                  textClassName={'text-[15px] font-bold text-center'}
                  text={languageTexts.inspection_save_button_text}
                  onClick={() => {
                    showSaveModal();
                  }}
                />
                <Button
                  className="py-3 max-w-[280px] flex flex-row justify-center items-center"
                  buttonColor={'black'}
                  textColor={'white'}
                  textClassName={'text-[15px] font-bold text-center'}
                  text={languageTexts.inspection_store_button_text}
                  onClick={() => {
                    showStoreModal();
                  }}
                />
              </div>
              <div className="flex flex-row justify-center gap-2.5 pb-[88px]">
                <div className="w-full max-w-[280px]">{/* DUMMY SPACE */}</div>
                <FeedbackRemainTimer
                  render={(remainTimeStr) => (
                    <div className="mt-[10px] w-full max-w-[280px] text-center text-[15px]">
                      {languageTexts.inspection_remain_time_2(remainTimeStr)}
                    </div>
                  )}
                  dueDate={posting?.rental?.inspectionFeedbackDueDt}
                  onExpired={undefined} // 타이틀의 <FeedbackRemainTimer />에서만 onExpired 실행
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 이미지 미리보기 모달 */}
      {isPreviewVisible && typeof previewImage === 'object' ? (
        <div
          className="absolute top-0 left-0 right-0 bottom-0 flex items-center justify-center z-50 animate__animated animate__fadeIn"
          onClick={() => {
            handlePreviewModal(MODAL_TYPE.PREVIEW, 'end');
          }}
        >
          <img
            className="p-5 cursor-pointer bg-white shadow-lg"
            src={previewImage.url}
            alt="Preview"
            style={{ maxWidth: '90vw', maxHeight: '60vh' }}
            onMouseLeave={() => {
              handlePreviewModal(MODAL_TYPE.PREVIEW, 'end');
            }}
          />
        </div>
      ) : null}
    </div>
  );
};

export default connect(mapStateToProps, mapDispatchProps)(PostingFeedback);
